import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { environment } from 'src/environments/environment';
import { User } from '../models/user';
import { AuthorizationService } from './authorization.service';
import { IndexedStorageService } from './indexed-storage.service';

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  readonly API_URL: string = environment.apiUri;
  readonly LANGUAGE_VERSION_KEY: string = 'langVersion';
  readonly LANGUAGE_VERSION: number = 14.0;

  public currentUser: Observable<User>;
  readonly currentUserSubject: BehaviorSubject<User>;
  headers: HttpHeaders = new HttpHeaders();

  constructor(
    readonly http: HttpClient,
    readonly authenticationService: AuthenticationService,
    readonly authorizationService: AuthorizationService,
    private indexedStorageService: IndexedStorageService,
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(null);
    this.currentUser = this.currentUserSubject.asObservable();
    this.headers = new HttpHeaders({
      "Content-Type": "application/json; charset=UTF-8"
    });
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  login(user: User): Observable<any> {
    const body = {
      username: user.username,
      password: user.password
    }

    return this.http.post<any>(this.API_URL + "/auth", body, { headers: this.headers, withCredentials: true })
      .pipe(switchMap(response => this.authenticationService.ensureSession().pipe(map(() => {
        const userInfo = this.authenticationService.getCurrentUserInfo();
        if (userInfo) {
          this.authenticationService.setCurrentUserInfo(userInfo);
          this.authorizationService.loadPermissions(userInfo.roles || [])
          this.currentUserSubject.next(userInfo);
          this.updateLanguageData();
        }
        return response;
      }))));
  }

  updateLanguageData() {
    const storedLangVerStr = localStorage.getItem(this.LANGUAGE_VERSION_KEY);
    const storedLangVer = storedLangVerStr ? parseFloat(storedLangVerStr) : 0;
    
    if (this.LANGUAGE_VERSION > storedLangVer) {
      this.indexedStorageService.getLanguagesData().subscribe({
        next: (response) => {
          const languages = response?.languages || [];
          if (languages && Array.isArray(languages)) {
            languages.forEach(lang => {
              if (lang && lang.code) {
                this.indexedStorageService.refreshLocale(lang.code).subscribe({
                  error: (e) => {
                    console.warn(`Failed to refresh locale ${lang.code} after version check`, e);
                  }
                });
              }
            });
          }
          localStorage.setItem(this.LANGUAGE_VERSION_KEY, this.LANGUAGE_VERSION.toString());
        },
        error: (e) => {
          console.warn('Failed to get languages for refresh', e);
          localStorage.setItem(this.LANGUAGE_VERSION_KEY, this.LANGUAGE_VERSION.toString());
        }
      });
    }
  }

}
